import React from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import {
  ArrowTopRightOnSquareIcon,
  PrinterIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import {
  AWARDED_NET_DAYS,
  CASH_RECEIVED_NET_DAYS,
  INTEREST_GRACE_DAYS,
  buildAccountStatementGuideHref,
  buildDueScheduleGroups,
  formatArCurrency,
} from "../utils/accountsReceivable.js";

const DUE_BUCKET_BADGE = {
  today: "bg-gray-900 text-white",
  later: "bg-gray-100 text-gray-700",
  paid: "bg-emerald-50 text-emerald-700",
};

const DUE_BUCKET_LABEL = {
  today: "Due today",
  later: "Coming due later",
  paid: "Paid",
};

const parseArDateValue = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const str = String(value).trim();
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let d;
  if (match) {
    d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  } else {
    d = new Date(str);
  }
  if (Number.isNaN(d.getTime())) return null;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const formatArDateHuman = (value) => {
  if (!value) return "—";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "—";
    return value.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  const str = String(value).trim();
  const match = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let d;
  if (match) {
    d = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  } else {
    d = new Date(str);
  }
  if (Number.isNaN(d.getTime())) return str;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

function scheduleGroupTitle(group) {
  if (group.kind === "retainer") {
    return group.description || "Retainer";
  }
  return (
    group.upgradeName ||
    group.opportunityName ||
    group.description ||
    "Opportunity"
  );
}

function scheduleGroupSubtitle(group) {
  const title = scheduleGroupTitle(group);
  if (
    group.opportunityName &&
    group.opportunityName !== title
  ) {
    return group.opportunityName;
  }
  return null;
}

function scheduleGroupFacts(group) {
  const facts = [];
  if (group.kind === "retainer") {
    if (group.monthLabel) {
      facts.push({ label: "Month", value: group.monthLabel });
    }
    facts.push({
      label: "Retainer",
      value: formatArCurrency(group.feeAmount),
    });
    if (group.termsDueDate) {
      facts.push({
        label: "Due",
        value: formatArDateHuman(group.termsDueDate),
      });
    }
    return facts;
  }

  if (group.hoursBased) {
    if (group.quantity > 0 && group.unitAmount > 0) {
      facts.push({
        label: "Hours",
        value: `${group.quantity} hrs × ${formatArCurrency(group.unitAmount)}/hr`,
      });
    }
    facts.push({
      label: "Cancellation fee",
      value: formatArCurrency(group.feeAmount),
    });
    return facts;
  }

  if (group.awarded > 0) {
    facts.push({
      label: "Awarded",
      value: formatArCurrency(group.awarded),
    });
  }
  const feePercent =
    Number.isFinite(group.feePercent) && group.feePercent > 0
      ? `${Math.round(group.feePercent)}% of awarded`
      : group.feeNote;
  facts.push({
    label: "Success fee",
    value: [feePercent, formatArCurrency(group.feeAmount)]
      .filter(Boolean)
      .join(" · "),
  });
  if (group.appliedDate) {
    facts.push({
      label: "Applied",
      value: formatArDateHuman(group.appliedDate),
    });
  }
  if (group.awardedDate) {
    facts.push({
      label: "Awarded on",
      value: formatArDateHuman(group.awardedDate),
    });
  }
  if (group.cashReceivedDate) {
    facts.push({
      label: "Cash received",
      value: formatArDateHuman(group.cashReceivedDate),
    });
  }
  if (group.termsDueDate) {
    facts.push({
      label: "Terms due",
      value: formatArDateHuman(group.termsDueDate),
    });
  }
  if (group.planMonths > 1) {
    facts.push({
      label: "Payment plan",
      value: `${group.planMonths} monthly installments`,
    });
  }
  return facts;
}

export function DueCalculationExplainerBody({
  doc,
  asOf = new Date(),
  showHeader = true,
}) {
  const asOfDate = parseArDateValue(asOf) || parseArDateValue(new Date());
  const asOfLabel = formatArDateHuman(asOfDate);
  const groups = buildDueScheduleGroups(doc?.lineItems, asOfDate);
  const totalAwarded = Number(doc?.totalAwarded) || 0;
  const dueToday = Number(doc?.dueToday) || 0;
  const dueLater = Number(doc?.dueLater) || 0;
  const balance =
    doc?.balanceDue != null
      ? Number(doc.balanceDue)
      : Math.max(
          0,
          (Number(doc?.totalDue) || 0) - (Number(doc?.paidToDate) || 0)
        );
  const paid = Number(doc?.paidToDate) || 0;
  const interest = Number(doc?.interestAccrued) || 0;
  const hasSplit = Boolean(doc?.showDueToday) || dueLater > 0.009;
  const companyName = doc?.billTo?.name && doc.billTo.name !== "—"
    ? doc.billTo.name
    : "Client";

  return (
    <div className="space-y-8">
      {showHeader ? (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            {doc?.seller?.name || "Upgraded"}
          </p>
          <h2 className="mt-1 text-xl font-semibold tracking-tight text-gray-900">
            How your balance is calculated
          </h2>
          <p className="mt-1 text-sm text-gray-600">
            {companyName}
            {doc?.meta?.invoiceNumber
              ? ` · Invoice #${doc.meta.invoiceNumber}`
              : ""}
            {` · As of ${asOfLabel}`}
          </p>
        </div>
      ) : null}

      <div
        className={`grid gap-3 ${
          hasSplit ? "sm:grid-cols-3" : "sm:grid-cols-2"
        }`}
      >
        {hasSplit ? (
          <>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Due today
              </p>
              <p className="mt-1 text-lg font-semibold tabular-nums tracking-tight text-gray-900">
                {formatArCurrency(dueToday)}
              </p>
              <p className="mt-1 text-xs text-gray-500">Payable now</p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Coming due later
              </p>
              <p className="mt-1 text-lg font-semibold tabular-nums tracking-tight text-gray-900">
                {formatArCurrency(dueLater)}
              </p>
              <p className="mt-1 text-xs text-gray-500">Still scheduled</p>
            </div>
          </>
        ) : null}
        <div className="rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Balance remaining
          </p>
          <p className="mt-1 text-lg font-semibold tabular-nums tracking-tight text-gray-900">
            {formatArCurrency(balance)}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {hasSplit
              ? "Due today + coming due later"
              : "Amount still outstanding"}
          </p>
        </div>
      </div>

      <section className="space-y-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">
            Payment schedule
          </h3>
          {totalAwarded > 0 ? (
            <p className="mt-1 text-sm text-gray-600">
              {`${formatArCurrency(totalAwarded)} awarded${
                groups.filter((g) => g.kind !== "retainer").length > 1
                  ? ` across ${
                      groups.filter((g) => g.kind !== "retainer").length
                    } opportunities`
                  : ""
              }. Each line shows the grant, awarded amount, dates, and how the fee is split.`}
            </p>
          ) : null}
        </div>
        {groups.length === 0 ? (
          <p className="text-sm text-gray-500">
            No unpaid fees or retainers are on this statement.
          </p>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => {
              const title = scheduleGroupTitle(group);
              const subtitle = scheduleGroupSubtitle(group);
              const facts = scheduleGroupFacts(group);
              return (
                <div
                  key={group.key}
                  className="overflow-hidden rounded-xl border border-gray-100"
                >
                  <div className="border-b border-gray-100 bg-gray-50 px-4 py-4 sm:px-5">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {title}
                    </h4>
                    {subtitle ? (
                      <p className="mt-0.5 text-sm text-gray-600">
                        {subtitle}
                      </p>
                    ) : null}
                    {facts.length ? (
                      <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
                        {facts.map((fact) => (
                          <div
                            key={fact.label}
                            className="flex items-baseline justify-between gap-3"
                          >
                            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                              {fact.label}
                            </dt>
                            <dd className="text-sm tabular-nums text-gray-900">
                              {fact.value}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    ) : null}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100">
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                          {group.planMonths > 1
                            ? "Installment"
                            : "Charge"}
                        </th>
                        <th className="px-4 py-2.5 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                          Due
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                          Amount
                        </th>
                        <th className="px-4 py-2.5 text-right text-xs font-medium uppercase tracking-wide text-gray-500">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {group.rows.map((row) => (
                        <tr key={row.key}>
                          <td className="px-4 py-3 text-gray-900">
                            {row.detail}
                          </td>
                          <td className="px-4 py-3 tabular-nums text-gray-600">
                            {row.dueDate
                              ? formatArDateHuman(row.dueDate)
                              : "—"}
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-900">
                            {formatArCurrency(
                              row.bucket === "paid"
                                ? row.amount
                                : row.unpaid || row.amount
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                DUE_BUCKET_BADGE[row.bucket] ||
                                DUE_BUCKET_BADGE.later
                              }`}
                            >
                              {DUE_BUCKET_LABEL[row.bucket] || row.bucket}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
        <p className="text-xs text-gray-400 leading-relaxed">
          Schedule amounts are fees before tax. Tax is included in the Due
          today and Coming due later totals in proportion to those fees.
          {paid > 0
            ? ` Payments received (${formatArCurrency(
                paid
              )}) are applied to due today first.`
            : ""}
        </p>
      </section>

      <section className="space-y-3 border-t border-gray-100 pt-6">
        <h3 className="text-base font-semibold text-gray-900">The record</h3>
        <div className="space-y-3 text-sm text-gray-600 leading-relaxed">
          <p>
            <span className="font-medium text-gray-900">Due today</span> is
            everything that is payable right now: unpaid installments and
            retainers whose date is today or earlier, plus their share of tax{interest > 0
              ? ", plus any interest already accrued"
              : ""}
            {paid > 0
              ? ". Payments already received are applied here first."
              : "."}
          </p>
          <p>
            <span className="font-medium text-gray-900">
              Coming due later
            </span>{" "}
            is the rest of the unpaid schedule — future payment-plan
            installments and retainers after today, plus their share of tax.
            Those amounts stay on the overall balance, but they are not part of
            what is payable today.
          </p>
          <p>
            A success fee is due on the earlier of{" "}
            <span className="font-medium text-gray-900">
              {CASH_RECEIVED_NET_DAYS} days after cash is received from the
              funder
            </span>{" "}
            or{" "}
            <span className="font-medium text-gray-900">
              {AWARDED_NET_DAYS} days after the award date
            </span>
            . If there is no multi-month plan, the full unpaid fee is included
            in due today. A payment plan splits that fee into equal monthly
            installments starting on the awarded date.
          </p>
          <p>
            Interest, if any, starts {INTEREST_GRACE_DAYS} days after an
            amount’s due date at 1% per month, and is always included in due
            today. Paying now reduces due today first, then coming due later.
          </p>
        </div>
      </section>
    </div>
  );
}

export default function DueCalculationExplainer({
  open,
  onClose,
  doc,
  asOf = new Date(),
}) {
  const guideHref = buildAccountStatementGuideHref({
    website: doc?.billTo?.website,
    companyName: doc?.billTo?.name,
  });

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      {open ? (
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            #ar-due-explainer-print,
            #ar-due-explainer-print * { visibility: visible !important; }
            #ar-due-explainer-print {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              max-height: none !important;
              overflow: visible !important;
            }
          }
        `}</style>
      ) : null}
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-900/20 transition-opacity data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in"
      />
      <div className="fixed inset-0 z-10 overflow-y-auto p-4 sm:p-6">
        <div className="flex min-h-full items-center justify-center">
          <DialogPanel
            transition
            className="w-full max-w-4xl transform overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl outline-none transition-all data-closed:scale-95 data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-150 data-leave:ease-in"
          >
            <div id="ar-due-explainer-print">
              <div className="border-b border-gray-100 px-6 py-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      {doc?.seller?.name || "Upgraded"}
                    </p>
                    <DialogTitle className="mt-1 text-xl font-semibold tracking-tight text-gray-900">
                      How your balance is calculated
                    </DialogTitle>
                    <p className="mt-1 text-sm text-gray-600">
                      {doc?.billTo?.name && doc.billTo.name !== "—"
                        ? doc.billTo.name
                        : "Client"}
                      {doc?.meta?.invoiceNumber
                        ? ` · Invoice #${doc.meta.invoiceNumber}`
                        : ""}
                      {` · As of ${formatArDateHuman(
                        parseArDateValue(asOf) || new Date()
                      )}`}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="print:hidden shrink-0 rounded-lg p-1.5 text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
                    aria-label="Close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <div className="max-h-[75vh] overflow-y-auto px-6 py-6 print:max-h-none print:overflow-visible">
                <DueCalculationExplainerBody
                  doc={doc}
                  asOf={asOf}
                  showHeader={false}
                />
              </div>
            </div>

            <div className="print:hidden flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-6 py-4">
              <p className="text-xs text-gray-400">
                Designed to screenshot or print for clients
              </p>
              <div className="flex items-center gap-2">
                <a
                  href={guideHref}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  Client fee page
                </a>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <PrinterIcon className="h-3.5 w-3.5" />
                  Print
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
